import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { useRouteMatch } from 'react-router-dom';
import { Typography } from '@material-ui/core';

import {
  PublicUserProfileFragment,
  UserFeedFollowFragment,
  UserFeedFollowGroupFragment,
} from 'api';
import { Layouts } from 'components';
import { useCurrentUser } from 'hooks';
import { GroupListFollow } from 'features/groups';
import * as paths from 'paths';

import ProfileList from '../ProfileList';
import Header from './Header';
import Activity from './Activity';

interface IProps {
  user: PublicUserProfileFragment;
  followedResults: UserFeedFollowFragment[];
  followersResults: PublicUserProfileFragment[];
}

const Controller: FC<IProps> = ({ user, followedResults, followersResults }) => {
  const currentUser = useCurrentUser();
  const match = useRouteMatch();
  const { t } = useTranslation();

  const isActivity = match.path === paths.profile;
  const isGroups = match.path === paths.profileGroups;
  const isFollowing = match.path === paths.profileFollowing;
  const isFollowers = match.path === paths.profileFollowers;

  const isCurrentUser = user.id === currentUser.id;
  const followedUsers = getFollowedUsers();
  const followedGroups = getFollowedGroups();

  function getFollowedUsers() {
    const users: PublicUserProfileFragment[] = [];

    followedResults.forEach(follow => {
      follow.user && users.push(follow.user);
    });

    return users;
  }

  function getFollowedGroups() {
    const groups: UserFeedFollowGroupFragment[] = [];

    followedResults.forEach(follow => {
      follow.group && groups.push(follow.group);
    });

    return groups;
  }

  function renderGroups() {
    const emptyStateName = isCurrentUser ? 'myProfileGroups' : 'profileGroups';

    return (
      <Fragment>
        <Typography paragraph variant="subtitle2">
          {t('components:xGroup', { count: followedGroups.length })}
        </Typography>
        <GroupListFollow emptyStateName={emptyStateName} groups={followedGroups} />
      </Fragment>
    );
  }

  function renderFollowing() {
    const emptyStateName = isCurrentUser ? 'myProfileFollowing' : 'profileFollowing';

    return (
      <Fragment>
        <Typography paragraph variant="subtitle2">
          {t('components:xPerson', { count: followedUsers.length })}
        </Typography>
        <ProfileList emptyStateName={emptyStateName} profiles={followedUsers} />
      </Fragment>
    );
  }

  function renderFollowers() {
    const emptyStateName = isCurrentUser ? 'myProfileFollowers' : 'profileFollowers';

    return (
      <Fragment>
        <Typography paragraph variant="subtitle2">
          {t('components:xPerson', { count: followersResults.length })}
        </Typography>
        <ProfileList emptyStateName={emptyStateName} profiles={followersResults} />
      </Fragment>
    );
  }

  if (isActivity) {
    const emptyStateName = isCurrentUser ? 'myProfileActivity' : 'profileActivity';

    return (
      <Layouts.App1Column
        desktopSize={10}
        main={
          <Activity
            emptyStateName={emptyStateName}
            header={<Header profile={user} />}
            profile={user}
            groups={followedGroups}
            users={followedUsers}
            followedByCount={followersResults.length}
            followedByUsers={followersResults}
          />
        }
      />
    );
  }

  let main;

  if (isGroups) {
    main = renderGroups();
  }

  if (isFollowing) {
    main = renderFollowing();
  }

  if (isFollowers) {
    main = renderFollowers();
  }

  if (!main) {
    return <Redirect to={paths.people} />;
  }

  return (
    <Layouts.App1Column
      desktopSize={10}
      main={<Layouts.AppMain1Column header={<Header profile={user} />} main={main} />}
    />
  );
};

export default Controller;
